import React from "react";
import { useConfig } from "../api";
import { useAuth } from "../api";
import type { ZekayConfig } from "api/config/Config.typed";
import { zekayStaticBranding } from "../../config/staticFiles";
import { companyLogoImageSources, prefetchImageUrl } from "../../utils/companyLogoUrl";
import { AppConfigContext } from "./appConfig";
import { CompanyModel } from "../../api/company/CompanyModel";
import { applyZekayThemePreset, normalizePresetFromApi } from "../../styles/themePresets";

type ToggleField = "AutomaticMailing" | "ManageGroups" | "CustomersSync";

export interface AppSettingsContextType {
  config: ZekayConfig | null;
  loading: boolean;
  configReadyForMenu: boolean;
  brandLogoSrc: string;
  onBrandLogoImgError: () => void;
  hasCompanyLogo: boolean;
  reloadConfig: () => Promise<void>;
  saveConfig: (cfg?: ZekayConfig) => Promise<void>;
  setToggle: (field: ToggleField, value: boolean) => Promise<void>;
  setConfigLocal: (updater: (prev: ZekayConfig) => ZekayConfig) => void;
}

export const AppSettingsContext = React.createContext<AppSettingsContextType>(null!);

export function AppSettingsProvider({ children }: { children: React.ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { getConfig, updateConfig } = useConfig();
  const { isLogged, user } = useAuth();
  const [config, setConfig] = React.useState<ZekayConfig | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [configAppliedCompanyId, setConfigAppliedCompanyId] = React.useState<number | undefined>(
    undefined,
  );
  const requestTicketRef = React.useRef(0);

  const defaultZekayMark = React.useMemo(() => zekayStaticBranding.zekayIconPng(), []);

  const companyId = user?.Role?.company_id ?? user?.company_id;
  const rawLogo = (config?.LogoUrl || "").trim();

  const companyModel = React.useMemo(
    () => (appConfig != null ? new CompanyModel(appConfig) : null),
    [appConfig],
  );

  React.useEffect(() => {
    if (!isLogged || companyId == null || companyModel == null) {
      applyZekayThemePreset("ocean_blue");
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const c = await companyModel.getCurrentCompany();
        if (cancelled) return;
        applyZekayThemePreset(normalizePresetFromApi(c?.ui_theme_preset));
      } catch {
        if (!cancelled) applyZekayThemePreset("ocean_blue");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLogged, companyId, companyModel]);

  const computedBrandLogoSrc = React.useMemo(() => {
    if (!rawLogo) {
      return defaultZekayMark;
    }
    return companyLogoImageSources(rawLogo).primary;
  }, [rawLogo, defaultZekayMark]);

  const [brandLogoErrorFallback, setBrandLogoErrorFallback] = React.useState<string | null>(null);

  React.useEffect(() => {
    setBrandLogoErrorFallback(null);
  }, [computedBrandLogoSrc]);

  const brandLogoSrc = brandLogoErrorFallback ?? computedBrandLogoSrc;

  const onBrandLogoImgError = React.useCallback(() => {
    setBrandLogoErrorFallback((prev) => {
      const current = prev ?? computedBrandLogoSrc;
      if (!rawLogo) {
        return current;
      }
      const { primary, fallback } = companyLogoImageSources(rawLogo);
      if (!fallback) {
        return prev ?? computedBrandLogoSrc;
      }
      return current === primary ? fallback : current;
    });
  }, [rawLogo, computedBrandLogoSrc]);

  React.useLayoutEffect(() => {
    prefetchImageUrl(brandLogoSrc);
  }, [brandLogoSrc]);

  const reloadConfig = React.useCallback(async () => {
    if (!isLogged) return;
    const targetId = user?.Role?.company_id ?? user?.company_id;
    if (targetId == null) return;
    const ticket = ++requestTicketRef.current;
    setLoading(true);
    try {
      const cfg = await getConfig();
      if (ticket !== requestTicketRef.current) return;
      const rawLogoAfter = (cfg?.LogoUrl || "").trim();
      if (rawLogoAfter) {
        prefetchImageUrl(companyLogoImageSources(rawLogoAfter).primary);
      } else {
        prefetchImageUrl(zekayStaticBranding.zekayIconPng());
      }
      setConfig(cfg ?? null);
      setConfigAppliedCompanyId(targetId);
    } catch {
      if (ticket !== requestTicketRef.current) return;
      setConfig(null);
      setConfigAppliedCompanyId(targetId);
    } finally {
      if (ticket === requestTicketRef.current) {
        setLoading(false);
      }
    }
  }, [getConfig, isLogged, user]);

  const saveConfig = React.useCallback(
    async (cfg?: ZekayConfig) => {
      const next = cfg ?? config;
      if (!next) return;
      await updateConfig(next);
      setConfig(next);
    },
    [config, updateConfig],
  );

  const setToggle = React.useCallback(
    async (field: ToggleField, value: boolean) => {
      if (!config) return;
      const next = { ...config, [field]: value } as ZekayConfig;
      setConfig(next);
      await updateConfig(next);
    },
    [config, updateConfig],
  );

  const setConfigLocal = React.useCallback((updater: (prev: ZekayConfig) => ZekayConfig) => {
    setConfig((prev) => (prev ? updater(prev) : prev));
  }, []);

  React.useEffect(() => {
    if (!isLogged || companyId == null) {
      requestTicketRef.current += 1;
      setConfig(null);
      setConfigAppliedCompanyId(undefined);
      setLoading(false);
      return;
    }
    if (configAppliedCompanyId === companyId) return;
    void reloadConfig();
  }, [isLogged, companyId, configAppliedCompanyId, reloadConfig]);

  const configReadyForMenu =
    !isLogged || companyId == null || (configAppliedCompanyId === companyId && !loading);

  const hasCompanyLogo = Boolean(rawLogo);

  const value = React.useMemo(
    (): AppSettingsContextType => ({
      config,
      loading,
      configReadyForMenu,
      brandLogoSrc,
      onBrandLogoImgError,
      hasCompanyLogo,
      reloadConfig,
      saveConfig,
      setToggle,
      setConfigLocal,
    }),
    [
      config,
      loading,
      configReadyForMenu,
      brandLogoSrc,
      onBrandLogoImgError,
      hasCompanyLogo,
      reloadConfig,
      saveConfig,
      setToggle,
      setConfigLocal,
    ],
  );

  return <AppSettingsContext.Provider value={value}>{children}</AppSettingsContext.Provider>;
}

export default function useAppSettings() {
  const ctx = React.useContext(AppSettingsContext);
  if (!ctx) throw new Error("useAppSettings must be used within AppSettingsProvider");
  return ctx;
}
